import first from 'lodash/first';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@spotnana/blocks/src/Divider';
import { margins } from '@spotnana/blocks/src/utils/styling';
import Box from '@spotnana/pixel-react/dist/Box';
import {
  AirSearchResponseManager,
  AirSelectedItineraryResponseManager,
  FlightDetailsInfoType,
  IFlightLayoverInfo,
  IFlightWithAmenitiesInfo,
} from 'obt-common';
import { RateOptionTicketType } from 'obt-common/types/api/v1/obt/air/air_search_response';

import { PreferredLabel } from 'src/v2-components/shared/PreferredLabel/PreferredLabel';
import OutOfPolicyModal from '../../../../v2-components/shared/OutOfPolicyModal';
// TODO: CONVENTION_VIOLATIONS: Why is this not in a shared folder
import LayoverInfo from '../../FlightSearchResultsPageV2/FlightDetailsModal/FlightLayoverInfo/LayoverInfo';
import { ItineraryGreaterThanLLFAlert } from '../../checkout/AncillaryPage/ItineraryGreaterThanLLFAlert';
import MixedCabinWarning from '../../checkout/AncillaryPage/MixedCabinWarning';
import FlightSegmentDetails from '../../checkout/shared/components/FlightSegmentDetails';
import AmenitiesAndFareRules, { FareRulesRequestIdInfo } from '../AmenitiesAndFareRules';
import { CheckoutInfoContainer } from '../CheckoutInfoContainer';
import OriginToDestinationHeading from '../OriginToDestinationHeading';

import { checkout_container } from './index.styles';

interface IProps {
  airSelectedItineraryResponseManager: AirSelectedItineraryResponseManager;
  legIndex: number;
  legNumber: number;
  showFareRulesAndAmenities: boolean;
  fareRulesRequestId: FareRulesRequestIdInfo;
  airlineReference: string;
  shouldShowLLFAlert: boolean;
  onFareRulesAndAmenitiesClick?: VoidFunction;
  onOOPClick?: VoidFunction;
}

const getFlightDetails = (legFlightDetails: IFlightLayoverInfo[]) =>
  legFlightDetails
    .filter(({ type }) => type === FlightDetailsInfoType.FLIGHT)
    .map((item) => item.data as IFlightWithAmenitiesInfo);

const LegDetails: React.FC<IProps> = ({
  airSelectedItineraryResponseManager,
  legNumber,
  legIndex,
  showFareRulesAndAmenities,
  fareRulesRequestId,
  airlineReference,
  shouldShowLLFAlert,
  onFareRulesAndAmenitiesClick,
  onOOPClick,
}: IProps) => {
  const { t: tt } = useTranslation('WEB');

  const legFlightDetails = airSelectedItineraryResponseManager.GetLegFlightDetailsForSelectedItinerary(legIndex);
  const flightDetails = getFlightDetails(legFlightDetails);
  const [showLLFBannerAlert, setShowLLFBannerAlert] = useState(true);

  const legDepartureDateTime = flightDetails[0].departureDateTime;
  const originAirport = `${flightDetails[0].departureAirportCity} (${flightDetails[0].departureAirport})`;
  const NumberOfFlights = flightDetails.length;
  const destinationAirport = `${flightDetails[NumberOfFlights - 1].arrivalAirportCity} (${
    flightDetails[NumberOfFlights - 1].arrivalAirport
  })`;
  const thirdPartySource = airSelectedItineraryResponseManager.GetItinerarySourcePerLegInfo(legNumber);
  const isOutOfPolicy = airSelectedItineraryResponseManager.isOutOfPolicy(legNumber);
  const policyInfo = airSelectedItineraryResponseManager.getPolicyInfoForLeg(legNumber);
  const preferences = airSelectedItineraryResponseManager.getLegPreferences({
    itinIndex: 0,
    legNumber,
    rateOptionIndex: 0,
  });

  const rateOptionAirline = airSelectedItineraryResponseManager.GetRateOptionAirline(0);

  const ticketType = airSelectedItineraryResponseManager.getTicketType();
  const origin = originAirport;
  const destination = destinationAirport;
  const brandName = airSelectedItineraryResponseManager.getBrandName(legNumber);
  const fareInfo = first(airSelectedItineraryResponseManager.getAllFaresForItineraryIndexEnhanced(0, legIndex));
  const hasPolicyInfo = !!policyInfo;
  const isMultiCity = ticketType === RateOptionTicketType.MULTI;
  const isSingleOOP = ticketType === RateOptionTicketType.SINGLE && isOutOfPolicy;
  const showOOPModal = hasPolicyInfo && (isSingleOOP || isMultiCity);

  const isMixedCabin = !!fareInfo && AirSearchResponseManager.getIsMixedCabin(fareInfo);

  const handleCloseLLFAlert = () => {
    setShowLLFBannerAlert(false);
  };

  return (
    <CheckoutInfoContainer
      data-testid="checkout_leg-summary_new"
      css={checkout_container(showLLFBannerAlert && shouldShowLLFAlert)}
    >
      {shouldShowLLFAlert && (
        <ItineraryGreaterThanLLFAlert
          airSelectedItineraryResponseManager={airSelectedItineraryResponseManager}
          handleClose={handleCloseLLFAlert}
          showLLFBannerAlert={showLLFBannerAlert}
        />
      )}
      <PreferredLabel preferences={preferences} />
      {isMixedCabin ? (
        <React.Fragment>
          <MixedCabinWarning fareInfo={fareInfo} />
          <Box mt={32} />
        </React.Fragment>
      ) : null}
      <OriginToDestinationHeading origin={origin} destination={destination}>
        {showOOPModal && (
          <div aria-label={tt('Policy Violation Message')}>
            <OutOfPolicyModal
              policyInfo={policyInfo}
              ticketType={ticketType}
              isOutOfPolicy={isOutOfPolicy}
              onOOPClick={onOOPClick}
            />
          </div>
        )}
      </OriginToDestinationHeading>
      <Divider />

      {legFlightDetails.map((legFlightDetail, idx) => {
        const hasNext = idx < legFlightDetails.length - 1;

        if (legFlightDetail.type === FlightDetailsInfoType.FLIGHT) {
          const flightNumber = flightDetails.indexOf(legFlightDetail.data);
          const bookingCode = airSelectedItineraryResponseManager.getBookingCode(legNumber, flightNumber);
          const cabin = airSelectedItineraryResponseManager.getFlightCabin(legNumber, flightNumber);
          const isUtaPassPlusRate = airSelectedItineraryResponseManager.getIsUatpPassPlusRate(legIndex);
          const utas = airSelectedItineraryResponseManager.GetLegUTAsForSelectedItinerary(legNumber);
          const showRawFareRules = airSelectedItineraryResponseManager.GetShowRawFareRulesForSelectedItinerary();

          const {
            departureDateTime,
            arrivalDateTime,
            arrivalAirport,
            arrivalAirportName,
            departureAirport,
            departureAirportName,
            airline,
            operatingAirlineLabel,
            duration,
            amenities,
            hiddenLayovers,
          } = legFlightDetail.data;

          return (
            <Fragment key={uniqueId()}>
              <FlightSegmentDetails
                flightDuration={duration}
                departureDateTime={departureDateTime}
                arrivalDateTime={arrivalDateTime}
                arrivalAirport={arrivalAirport}
                arrivalAirportName={arrivalAirportName}
                departureAirport={departureAirport}
                departureAirportName={departureAirportName}
                marketingAirlineNumber={airline?.marketing?.num}
                marketingAirlineLabel={airline?.marketing?.airline}
                operatingAirlineLabel={operatingAirlineLabel}
                legDepartureDateTime={legDepartureDateTime}
                brandName={brandName}
                bookingCode={bookingCode}
                cabin={cabin}
                isUtaPassPlusRate={isUtaPassPlusRate}
                isMixedCabin={isMixedCabin}
                flightSourceStatus=""
                hasExtraInfoAccess={false}
                itinerarySource={thirdPartySource}
                airlineReference={airlineReference}
                rateOptionAirline={rateOptionAirline}
                hiddenLayovers={hiddenLayovers}
              />
              {showFareRulesAndAmenities && (
                <AmenitiesAndFareRules
                  utas={utas}
                  showRawFareRules={showRawFareRules}
                  amenities={amenities}
                  fareRulesRequestId={fareRulesRequestId}
                  onFareRulesAndAmenitiesClick={onFareRulesAndAmenitiesClick}
                />
              )}
              {hasNext ? (
                <div css={margins.yt.m2}>
                  <Divider />
                </div>
              ) : null}
            </Fragment>
          );
        }

        return (
          <Fragment key={JSON.stringify(legFlightDetail.data)}>
            <LayoverInfo layover={legFlightDetail.data} />
            {hasNext ? <Divider /> : null}
          </Fragment>
        );
      })}
    </CheckoutInfoContainer>
  );
};

export default LegDetails;
